import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import styled from 'styled-components'
import { Container } from '../../components/Animations'
import { ButtonLeadingIcon, PrimaryButton, SecondaryButton } from '../../components/Button'
import ConfirmButtonModal from '../../components/ConfirmButtonModal'
import { DropdownItem } from '../../components/Dropdown'
import PageHeader from '../../components/PageHeader'
import { SelectedMethodIcon } from '../../components/PaymentMethodDisplay'
import Table from '../../components/Table'
import TimeText from '../../components/TimeText'
import CopyButton from '../../components/CopyButton'
import LinkExternal from '../../components/LinkExternal'
import AccountDetailsDrawer, { DrawerField } from '../../components/AccountDetailsDrawer'
import { StatusBadge } from '../Invoices'
import { ExternalLinkText } from '../Events'
import { RADOM_COLORS } from '../../util/Constants'
import Radom, { WithdrawalAccount, WithdrawalRequest } from '../../state/Radom'
import { generateRoundFlagIconFromCountryCode } from '../../util/Flags'
import { getMethod, withdrawalStatusDisplay } from '../../util/Managed'
import { infoToast, roundAssetAmount, shortAddress, successToast, useTestMode } from '../../util/Util'
import CreateWithdrawalAccountModal from './CreateWithdrawalAccountModal'
import { formatWithdrawalAccount, formatWithdrawalAccountType } from '../../components/Shared'
import {
  DashboardPage,
  InlinePill,
  PageActionRow,
  PageHeaderRow,
  SummaryLabel,
  SummaryMeta,
  SummaryStrip,
  SummaryStripItem,
  SummaryValue,
  Surface,
  SurfaceHeader,
  SurfaceTitle
} from '../../components/DashboardPrimitives'
import LoadingBar from '../../components/LoadingBar'
import Gate from '../../components/Gate'
import { usePermissions } from '../../util/permissions'

type AccountCategory = 'all' | 'crypto' | 'fiat'

const AccountCategoryControl = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border: 1px solid var(--border, #e3e7ef);
  border-radius: 11px;
  background: var(--surface-hover, #f7f9fc);
`

const AccountCategoryButton = styled.button<{ $active?: boolean }>`
  align-items: center;
  border: 0;
  border-radius: 9px;
  background: ${({ $active }) => $active ? 'var(--surface, #ffffff)' : 'transparent'};
  box-shadow: ${({ $active }) => $active ? '0 1px 2px rgba(16, 20, 31, 0.08)' : 'none'};
  color: ${({ $active }) => $active ? 'var(--ink-strong, #10141f)' : 'var(--text-muted, #6f7686)'};
  cursor: pointer;
  display: inline-flex;
  font-size: 11.5px;
  font-weight: 500;
  gap: 6px;
  line-height: 1;
  min-width: 76px;
  padding: 7px 10px;
  transition: background-color 0.16s ease, color 0.16s ease;

  &:hover {
    background: ${({ $active }) => $active ? 'var(--surface, #ffffff)' : 'var(--surface-pressed, #eef1f7)'};
    color: var(--ink-strong, #10141f);
  }
`

const DestinationIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`

const DestinationIdentityText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`

const DestinationName = styled.span`
  color: var(--ink, #20242d);
  font-size: 13.5px;
  font-weight: 500;
`

const DestinationMeta = styled.span`
  color: var(--text-soft, #7d8492);
  font-size: 12px;
  line-height: 1.35;
`

const TinyFlagStack = styled.div`
  align-items: center;
  display: flex;

  > * + * {
    margin-left: -5px;
  }
`

const Accounts = observer((): ReactElement => {
  const [isLoading, setIsLoading] = useState(true)
  const [isCreateWithdrawalModalOpen, setIsCreateWithdrawalModalOpen] = useState(false)
  const [withdrawalAccounts, setWithdrawalAccounts] = useState<WithdrawalAccount[]>([])
  const [accountCategory, setAccountCategory] = useState<AccountCategory>('all')
  const [testMode] = useTestMode()
  const [showDetailsPanel, setShowDetailsPanel] = useState(false)
  const navigate = useNavigate()
  const [selectedAccount, setSelectedAccount] = useState<WithdrawalAccount | null>(null)
  const [selectedTab, setSelectedTab] = useState<string>('Details')
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([])
  const [isLoadingWithdrawals, setIsLoadingWithdrawals] = useState(false)
  const { has } = usePermissions()

  const fetchWithdrawalAccounts = async (): Promise<void> => {
    setIsLoading(true)
    setWithdrawalAccounts([])
    Radom.listWithdrawalAccounts()
      .then((accs) => setWithdrawalAccounts(accs))
      .catch(err => {
        console.error('Failed to load withdrawal accounts', err)
        setWithdrawalAccounts([])
      })
      .finally(() => setIsLoading(false)
      )
  }

  const fetchWithdrawals = async (id: string): Promise<void> => {
    setIsLoadingWithdrawals(true)
    setWithdrawals([])
    Radom.listWithdrawalsByAccountId(id)
      .then(res => setWithdrawals(res))
      .catch(err => {
        console.error('Failed to load withdrawals', err)
        setWithdrawals([])
      })
      .finally(() => setIsLoadingWithdrawals(false)
      )
  }

  useEffect(() => {
    fetchWithdrawalAccounts()
  }, [testMode])

  const filteredWithdrawalAccounts = withdrawalAccounts
    .filter(t => {
      if (!t.accountDetails.Crypto) {
        return !testMode
      }

      const method = getMethod(t.accountDetails.Crypto.network, t.accountDetails.Crypto.token)
      return testMode ? !!method.isTestnet : !method.isTestnet
    })

  const cryptoAccountCount = filteredWithdrawalAccounts.filter(a => !!a.accountDetails.Crypto).length
  const fiatAccountCount = filteredWithdrawalAccounts.filter(a => !!a.accountDetails.Fiat).length
  const latestDestination = [...filteredWithdrawalAccounts]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
  const visibleWithdrawalAccounts = filteredWithdrawalAccounts.filter(account => {
    if (accountCategory === 'all') {
      return true
    }

    if (accountCategory === 'crypto') {
      return !!account.accountDetails.Crypto
    }

    return !!account.accountDetails.Fiat
  })

  const listTitle = accountCategory === 'crypto'
    ? 'Crypto addresses'
    : accountCategory === 'fiat'
      ? 'Bank accounts'
      : 'Saved accounts'

  const emptyPlaceholderText = accountCategory === 'crypto'
    ? 'No crypto accounts yet.'
    : accountCategory === 'fiat'
      ? 'No bank accounts yet.'
      : 'No withdrawal accounts yet.'

  return <Container>
    <DashboardPage>
      <PageHeaderRow>
        <PageHeader
          title='Withdrawal accounts' />
        <Gate permission='WriteWithdrawalAccount'>
          <PageActionRow>
            <PrimaryButton onClick={() => {
              setIsCreateWithdrawalModalOpen(true)
            }}>
              <ButtonLeadingIcon>+</ButtonLeadingIcon>
              <span>Create withdrawal account</span>
            </PrimaryButton>
          </PageActionRow>
        </Gate>
      </PageHeaderRow>

      <Surface>
        <SurfaceHeader>
          <div>
            <SurfaceTitle>Saved accounts</SurfaceTitle>
          </div>
          <InlinePill $tone={testMode ? 'amber' : 'green'}>
            {testMode ? 'Test mode' : 'Live mode'}
          </InlinePill>
        </SurfaceHeader>
        <SummaryStrip>
          <SummaryStripItem>
            <SummaryLabel>Total accounts</SummaryLabel>
            <SummaryValue>
              {isLoading ? <LoadingBar style={{ height: 28, width: 64 }} /> : filteredWithdrawalAccounts.length}
            </SummaryValue>
            <SummaryMeta>Saved</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Crypto</SummaryLabel>
            <SummaryValue>
              {isLoading ? <LoadingBar style={{ height: 28, width: 64 }} /> : cryptoAccountCount}
            </SummaryValue>
            <SummaryMeta>Wallets</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Bank</SummaryLabel>
            <SummaryValue>
              {isLoading ? <LoadingBar style={{ height: 28, width: 64 }} /> : fiatAccountCount}
            </SummaryValue>
            <SummaryMeta>Accounts</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Latest added</SummaryLabel>
            <SummaryValue>
              {isLoading
                ? <LoadingBar style={{ height: 28, width: 76 }} />
                : latestDestination
                  ? format(new Date(latestDestination.createdAt), 'MMM d')
                  : 'None'}
            </SummaryValue>
            <SummaryMeta>
              {latestDestination ? latestDestination.name : 'No destinations created yet'}
            </SummaryMeta>
          </SummaryStripItem>
        </SummaryStrip>
      </Surface>

      <Surface>
        <SurfaceHeader>
          <div>
            <SurfaceTitle>{listTitle}</SurfaceTitle>
          </div>
          <AccountCategoryControl aria-label='Saved account type'>
            {[
              ['all', 'All'],
              ['crypto', 'Crypto'],
              ['fiat', 'Bank']
            ].map(([value, label]) =>
              <AccountCategoryButton
                key={value}
                type='button'
                $active={accountCategory === value}
                onClick={() => setAccountCategory(value as AccountCategory)}
              >
                {value === 'crypto' && <SelectedMethodIcon method={getMethod('Bitcoin')} size={13} disableToolTip />}
                {value === 'fiat' && <TinyFlagStack>
                  {generateRoundFlagIconFromCountryCode('US', 13)}
                  {generateRoundFlagIconFromCountryCode('EU', 13)}
                </TinyFlagStack>}
                {label}
              </AccountCategoryButton>
            )}
          </AccountCategoryControl>
        </SurfaceHeader>
        <Table
          disableHoverStyle={!has('ReadWithdrawal')}
          onClickRow={(rowIndex: number) => {
            if (!has('ReadWithdrawal')) return
            const a = visibleWithdrawalAccounts[rowIndex]
            setSelectedTab('Details')
            setSelectedAccount(a)
            setShowDetailsPanel(true)
            if (withdrawals.length === 0 || withdrawals[0].withdrawalAccountId !== a.id) {
              fetchWithdrawals(a.id)
            }
          }}
          headers={['Account', 'Details', 'Created', ''].map((t, i) => <span key={i}>{t}</span>)}
          tableContainerStyle={{
            gridTemplateColumns: 'minmax(250px, 1.1fr) minmax(320px, 1.2fr) minmax(140px, 0.6fr) minmax(120px, 0.4fr)'
          }}
          items={
            visibleWithdrawalAccounts.map(a => {
              const method = a.accountDetails.Crypto
                ? getMethod(a.accountDetails.Crypto.network, a.accountDetails.Crypto.token)
                : undefined
              const fiatCountryCode = a.accountDetails.Fiat?.accountType.us ? 'US' : 'EU'
              const typeLabel = a.accountDetails.Crypto
                ? `${method?.name ?? a.accountDetails.Crypto.network}`
                : `${fiatCountryCode} bank account`

              return [
                <DestinationIdentity key={0}>
                  {a.accountDetails.Crypto && method
                    ? <SelectedMethodIcon method={method} size={26} disableToolTip />
                    : generateRoundFlagIconFromCountryCode(fiatCountryCode, 24)}
                  <DestinationIdentityText>
                    <DestinationName>{a.name}</DestinationName>
                    <DestinationMeta>{typeLabel}</DestinationMeta>
                  </DestinationIdentityText>
                </DestinationIdentity>,
                <div key={3} style={{ minWidth: 0 }}>
                  {formatWithdrawalAccount(a.accountDetails)}
                </div>,
                <TimeText key="txTime" date={new Date(a.createdAt)} />,
                <div key='send' style={{ display: 'flex', justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                  <Gate anyOf={['WriteWithdrawal', 'WritePayouts']}>
                    <SecondaryButton
                      style={{ padding: '7px 14px', fontSize: 13 }}
                      onClick={() => navigate(`/send?account=${a.id}`)}>
                      Send funds
                    </SecondaryButton>
                  </Gate>
                </div>
              ]
            }
            )
          }
          emptyPlaceholderText={emptyPlaceholderText}
          isLoading={isLoading} />
      </Surface>

      <AccountDetailsDrawer
        visible={showDetailsPanel}
        onClose={() => setShowDetailsPanel(false)}
        eyebrow='Withdrawal account'
        title={selectedAccount?.name}
        tabs={['Details', 'Withdrawals']}
        selectedTab={selectedTab}
        onTabChange={setSelectedTab}
        menu={selectedAccount && <>
          <DropdownItem onClick={() => {
            navigator.clipboard.writeText(selectedAccount.id)
            infoToast('Account ID copied to clipboard')
          }}>Copy account ID</DropdownItem>
          <Gate permission='WriteWithdrawalAccount'>
            <ConfirmButtonModal
              title='Delete account'
              description='Are you sure you want to delete this account?'
              action='Delete account'
              onConfirm={async () => {
                const id = selectedAccount.id
                setShowDetailsPanel(false)
                Radom.deleteWithdrawalAccount(id)
                  .then(async () => await fetchWithdrawalAccounts())
                  .then(() => successToast('Account deleted'))
              }}
              ActionComponent={(props: any) => <DropdownItem {...props}>Delete account</DropdownItem>}
            />
          </Gate>
        </>}
      >
        {selectedTab === 'Details'
          ? <>
            <DrawerField
              label='Account ID'
              mono
              value={selectedAccount?.id}
              copy={selectedAccount?.id ?? ''}
            />

            <DrawerField label='Type' boxed={false}>
              {selectedAccount && formatWithdrawalAccountType(selectedAccount.accountDetails)}
            </DrawerField>

            <DrawerField
              label={selectedAccount?.accountDetails.Crypto ? 'Withdrawal address' : 'Account details'}
              mono={!!selectedAccount?.accountDetails.Crypto}
              copy={selectedAccount?.accountDetails.Crypto?.address}
            >
              {selectedAccount && formatWithdrawalAccount(selectedAccount.accountDetails)}
            </DrawerField>

            <DrawerField label='Created'>
              {selectedAccount &&
                <TimeText textColor={RADOM_COLORS.BLACK} date={new Date(selectedAccount.createdAt)} />}
            </DrawerField>
          </>
          : <Table
            borderless
            disableOnClickState
            headers={['Withdrawal ID', 'Amount', 'Status', 'Date'].map(h => <span key={h}>{h}</span>)}
            isLoading={isLoadingWithdrawals}
            items={withdrawals.map(w => {
              const method = getMethod(w.network, w.token)
              return [
                <div key={'1'} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ExternalLinkText
                    onClick={e => e.stopPropagation()}
                    target="_blank"
                    href={`/withdrawal/${w.id}`}>
                    {shortAddress(w.id, 3)}
                    <LinkExternal stroke={RADOM_COLORS.BLACK} width={14} />
                  </ExternalLinkText>
                  <CopyButton text={w.id} />
                </div>,
                <div key={'2'} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {method.logo && <img src={method.logo} style={{ width: 20, height: 20 }} />}
                  <span>{roundAssetAmount(method.ticker, Number(w.withdrawalAmount))}</span>
                  <span>{method.ticker}</span>
                </div>,
                <div key={'3'}>
                  {/* awaitingInventory reads as an in-progress (gray) state, not the badge's red fallback. */}
                  <StatusBadge status={(w.withdrawalStatus === 'awaitingInventory' ? 'pending' : w.withdrawalStatus) as any}>
                    {withdrawalStatusDisplay(w.withdrawalStatus)}
                  </StatusBadge>
                </div>,
                <div key={'4'}>
                  <TimeText date={new Date(w.requestedAt)} />
                </div>
              ]
            })}
            emptyPlaceholderText='No withdrawals yet'
          />}
      </AccountDetailsDrawer>

      {
        isCreateWithdrawalModalOpen &&
        <CreateWithdrawalAccountModal
          presentation='drawer'
          title='Create withdrawal account'
          visible={isCreateWithdrawalModalOpen}
          onClose={() => setIsCreateWithdrawalModalOpen(false)}
          onAccountCreated={() => {
            setIsCreateWithdrawalModalOpen(false)
            fetchWithdrawalAccounts()
          }} />
      }
    </DashboardPage>
  </Container>
})

export default Accounts
